import { animate, state, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatNavList } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavigationService } from 'app/core/services/navigation.service';
import { UsersService } from 'app/features/user-management/services/users.service';
import { AuthorizationService } from 'core/services/auth/authorization.service';
import { ContextService } from 'core/services/context.service';
import { ThemeService } from 'core/services/theme.service';
import { environment } from 'environment/environment';
import { combineLatest, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { filter, shareReplay, startWith } from 'rxjs/operators';
import { ClientsService } from 'shared/services';
import { LaunchDarklyService } from 'shared/services/launch-darkly-service';
import { Client, NavigationItem, NavigationSection } from 'shared/types';
import { MenuDropdownItem } from 'shared/types/menu-dropdown-item';
import { UserInformation } from 'shared/types/user/user-information';
import { AuthClientService } from './features/user-management/services/auth-client.service';
import { DropdownMenuComponent, HeaderComponent } from './shared/components/molecules';
import { CustomRegex } from './shared/constants/custom-regex.const';
import { FirstLettersDirective } from './shared/directives';
import { ScreenSize } from './shared/enums';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  animations: [
    trigger('slideInOut', [
      state(
        'out',
        style({
          width: '300px',
        }),
      ),
      state(
        'in',
        style({
          width: '85px',
        }),
      ),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out')),
    ]),
    trigger('slideInOutContent', [
      state(
        'out',
        style({
          left: '300px',
        }),
      ),
      state(
        'in',
        style({
          left: '{{left}}',
        }),
        { params: { left: '2rem' } },
      ),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out')),
    ]),
  ],
  imports: [
    MatToolbarModule,
    NgClass,
    NgIf,
    MatIcon,
    HeaderComponent,
    AsyncPipe,
    MatSidenavModule,
    MatNavList,
    TranslateModule,
    DropdownMenuComponent,
    FirstLettersDirective,
    NgFor,
    RouterModule,
    MatMenuModule,
    MatDivider,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly router = inject(Router);
  protected readonly themeSvc = inject(ThemeService);
  protected readonly userService = inject(UsersService);
  protected readonly authorizationService = inject(AuthorizationService);
  protected readonly authClientService = inject(AuthClientService);
  protected readonly clientsService = inject(ClientsService);
  protected readonly authService = inject(AuthService);
  protected readonly contextService = inject(ContextService);
  protected readonly translate = inject(TranslateService);
  protected readonly navigationService = inject(NavigationService);
  protected readonly launchDarklyService = inject(LaunchDarklyService);

  public title = 'spotlight';
  public menuState = 'out';
  public environmentType = environment.type;
  public clientsMapped!: MenuDropdownItem[];

  public selectedItem!: NavigationItem;
  public isExpanded = true;
  public showHeaderOption = false;
  public isNavigationReady = false;
  public isTabletView = false;

  @ViewChild('sidenav')
  public sidenav!: MatSidenav;
  public clientsMapped$: Observable<MenuDropdownItem[]> = of([]);

  public navigation$: Observable<NavigationSection[]> = this.navigationService.navigationItems$;

  private destroy$ = new Subject<void>();

  public sites$ = combineLatest([
    this.contextService.clientId.pipe(filter((id) => id !== null)),
    this.contextService.sitesRefreshTrigger.asObservable().pipe(startWith(null)),
  ]).pipe(
    switchMap(([clientId]) => {
      return this.authorizationService.loggedInUser$.pipe(
        switchMap((user) => {
          if (this.authorizationService.isSuperAdmin()) {
            this.contextService.isSuperAdmin.next(true);
            return this.authClientService.getSitesByClient(clientId).pipe(map((response) => response.value));
          } else {
            if (user.clients && user.clients.length > 0) {
              this.contextService.isSuperAdmin.next(false);
              const filteredSites = user.clients
                .filter((client) => client.id === clientId)
                .flatMap((client) => client.sites || []);
              return of(filteredSites);
            } else {
              return of([]);
            }
          }
        }),
      );
    }),
    map((filteredSites) => {
      if (!filteredSites || filteredSites.length === 0) {
        this.contextService.sites.next([]);
        this.contextService.updateSiteContext(null);
        return [];
      }

      this.contextService.sites.next(filteredSites);

      const currentSiteId = this.contextService.siteId.getValue();
      const siteExists = filteredSites.find((site) => site.id === currentSiteId);

      if (!currentSiteId || !siteExists) {
        this.contextService.updateSiteContext(filteredSites[0]);
      }

      return filteredSites;
    }),
    switchMap(() =>
      this.contextService.sites.asObservable().pipe(
        map((sites) => {
          return sites || [];
        }),
      ),
    ),
    takeUntil(this.destroy$),
    shareReplay(1),
  );

  public clientId$ = this.contextService.clientId.asObservable().pipe(startWith(null));

  public navigationItems$: Observable<NavigationSection[]> = this.navigationService.navigationItems$;

  constructor() {
    this.translate.setDefaultLang('en');
    this.translate.use('en');
  }

  public ngOnInit(): void {
    this.initializeClients();
    this.setupResponsiveView();
    this.initializeNavigation();
    this.setupAuthentication();
    this.monitorClients();
  }

  private initializeClients(): void {
    this.clientsMapped$ = this.authorizationService.loggedInUser$.pipe(
      switchMap((userInfo) => this.fetchClients(userInfo)),
      tap((clients: Client[]) => this.setClients(clients)),
      map((clients: Client[]) => this.mapClientsToMenuItems(clients?.sort((a, b) => a.name.localeCompare(b.name)))),
      shareReplay(1),
      takeUntil(this.destroy$),
    );

    //TODO: Check why we still need to subcribe even though we are doing it in html with async pipe
    this.clientsMapped$.subscribe();
  }

  // If logged in user is SuperAdmin he should have access to all clients
  // Therefore, this method returns clients either from Site Management api (all clients) or auth0 /me endpoint
  private fetchClients(userInfo: UserInformation): Observable<Client[]> {
    return this.authorizationService.isSuperAdmin()
      ? this.authClientService.get<Client[]>().pipe(map(({ value }) => value))
      : of(userInfo.clients);
  }

  // Mapping clients to dropdown menu item interface
  private mapClientsToMenuItems(clients: Client[]): MenuDropdownItem[] {
    return clients?.map(
      (client) =>
        new MenuDropdownItem({
          id: client.id as string,
          label: client.name,
          subLabel: client.legalName || '',
        }),
    );
  }

  // If user tries to access our app from tablet and below, we should hide sidebar by default
  private setupResponsiveView(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const currentUrl = event.urlAfterRedirects;
      const isDetailsPage = CustomRegex.taskDetailsPage.test(currentUrl);
      const nonDesktopScreen = !this.themeSvc.isDesktop();

      if (nonDesktopScreen || (nonDesktopScreen && isDetailsPage)) {
        this.isExpanded = false;
        this.isTabletView = true;
        this.menuState = 'in';
      }
    });
  }

  // Set up navigation items based on user's role
  private initializeNavigation(): void {
    combineLatest([
      this.contextService.siteId.pipe(startWith(null)),
      this.userService.getLoggedInUserWithRoles().pipe(filter((userInfo) => !!userInfo)),
    ])
      .pipe(
        tap(() => (this.isNavigationReady = true)),
        switchMap(() => {
          const navigationItems = this.navigationService.getNavigationItems();
          const currentUrl = this.router.url;
          navigationItems.forEach((navigationItem) => {
            const preselectedItem = navigationItem.items?.find(
              (item) => item.routing && currentUrl.includes(item.routing),
            );
            if (preselectedItem) {
              this.selectedItem = preselectedItem;
            } else if (currentUrl.includes('admin-panel')) {
              this.selectedItem = {
                label: this.translate.instant('adminPanel.headline'),
                identifier: 'adminHeadline',
                routing: 'admin-panel',
                canView: () => true,
              };
            }
          });
          return navigationItems;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  // If user is authenticated redirect to targetUrl
  private setupAuthentication(): void {
    this.authService.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => this.authService.appState$),
        filter((appState) => !!appState),
        tap((appState) => {
          const targetUrl = appState.target || '/';
          if (targetUrl !== this.router.url) {
            this.router.navigateByUrl(targetUrl, { replaceUrl: true });
          }
        }),
      )
      .subscribe();
  }

  // Monitor client changes (if new one was added or so) and remap them into dropdown menu interface
  public monitorClients(): void {
    this.contextService.clients
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((clients) => {
        this.clientsMapped$ = of(
          this.mapClientsToMenuItems(clients?.sort((a, b) => a.name.localeCompare(b.name))),
        ).pipe(shareReplay(1), takeUntil(this.destroy$));
      });
  }

  // Takes clients from context if we reload the app previously selected client should stay
  private setClients(clients: Client[]): void {
    if (clients.length === 0) {
      this.contextService.clientId.next(null);
      this.contextService.clients.next([]);
      return;
    }
    this.contextService.clients.next(clients);

    if (
      this.contextService.clientId.getValue() &&
      clients.some((client) => client.id === this.contextService.clientId.getValue())
    ) {
      return;
    }

    const [selectedClient] = clients;
    this.contextService.clientId.next(selectedClient?.id as string);
  }

  public clientChanged(): void {
    if (this.isTabletView && this.isExpanded) {
      this.toggleMenu();
    }
    this.router.navigate(['/home']);
  }

  public toggleMenu(): void {
    this.isExpanded = !this.isExpanded;
    this.themeSvc.triggerResize();
    this.menuState = this.menuState === 'out' ? 'in' : 'out';
  }

  public showMobileHeaderOption(showHeaderOption: boolean): void {
    this.showHeaderOption = showHeaderOption;
  }

  public onItemSelected(item: NavigationItem): void {
    if (item.routing) {
      this.selectedItem = item;
    }
  }

  public navigateToAdminPanel(): void {
    this.selectedItem = {
      label: this.translate.instant('adminPanel.headline'),
      identifier: 'adminHeadline',
      routing: 'admin-panel',
      canView: () => true,
    };
    this.router.navigate(['/admin-panel']);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.launchDarklyService.close();
  }

  @HostListener('window:orientationchange', ['$event'])
  protected onOrientationChange(): void {
    this.themeSvc.triggerResize();
  }

  @HostListener('window:resize', ['$event'])
  protected onResize(): void {
    this.themeSvc.triggerResize();

    switch (this.themeSvc.screenSize) {
      case ScreenSize.DESKTOP:
        this.isExpanded = true;
        this.menuState = 'out';
        this.isTabletView = false;
        break;
      default:
        this.isExpanded = false;
        this.menuState = 'in';
        this.isTabletView = true;
    }
  }
}
